/**
 * Scene, camera, lighting, fog, IBL — the realism base (plan 02: baked IBL + PBR, fog,
 * ACES + vignette via image processing, no post pipeline in the slice) + the plan-05 P5.5
 * procedural PBR character materials (DynamicTexture atlas painting, 0 asset bytes).
 */
import {
  Constants,
  Color3,
  Color4,
  ColorCurves,
  DefaultRenderingPipeline,
  DirectionalLight,
  DynamicTexture,
  FreeCamera,
  HDRCubeTexture,
  ImageProcessingConfiguration,
  Mesh,
  MeshBuilder,
  PBRMaterial,
  RenderTargetTexture,
  Scene,
  ShadowGenerator,
  StandardMaterial,
  Texture,
  Vector3,
} from '@babylonjs/core/pure';
import type { AbstractEngine, AbstractMesh } from '@babylonjs/core/pure';
import type { AtlasTile } from './soldierAtlas';
import { tileRect, tileUvBounds } from './soldierAtlas';

export interface RenderMaterials {
  asphalt: PBRMaterial;
  concrete: PBRMaterial;
  /** Plan 08 G4: murky river; the Bridge scrolls its bump texture from sim.t and slides the plane with the squad. */
  water: PBRMaterial;
  metal: PBRMaterial;
  soldier: PBRMaterial;
  enemy: PBRMaterial;
  boss: PBRMaterial;
  /** Additive emissive (bullets, flashes, telegraphs). */
  glow: (color: Color3, alpha?: number) => StandardMaterial;
}

export interface WorldScene {
  scene: Scene;
  camera: FreeCamera;
  materials: RenderMaterials;
  /**
   * Plan 05 P5.6: the character shadow cascade, or null when the boot tier has no real shadows
   * (`caps.realShadows` false ⇒ the rig is never built, so no 1024² render target is allocated and
   * the blob shadows stay). Scene concerns stay here: the rig owns the light, the map and the
   * caster/receiver membership; main.ts only hands over meshes and the (sim-pure) squad position.
   */
  shadows: CharacterShadows | null;
  /** Plan 08 G1: the bloom pipeline (High + Medium), or null on Low. `pipeline.samples` is the effective MSAA. */
  pipeline: DefaultRenderingPipeline | null;
}

/**
 * Plan 17 D4: the environment theme — the single source of truth (world.ts imports this for the
 * Bridge horizon variant). `'overcast'` is today's L1 look verbatim; `'industrial'` is the L2
 * rain-grey/steel regrade + scrapyard horizon ring. Plan 19 D5 adds `'dusk'`: the L3 (Operation
 * Duskline) finale's lower light — warm amber-grey haze, dim orange sun, strictly darker than both
 * existing themes on exposure/sun intensity/environmentIntensity. The sun DIRECTION is pinned
 * across every theme (the plan-05 shadow rig's ortho box is measured against it — regrade only,
 * the plan-17 regrade-only precedent).
 */
export type EnvTheme = 'overcast' | 'industrial' | 'dusk';

export interface WorldSceneOptions {
  /** TierCaps.realShadows — High only (plan 05 Design 5). */
  realShadows: boolean;
  /** TierCaps.bloom — High + Medium (plan 08 G1). */
  bloom: boolean;
  /** TierCaps.msaaSamples — assigned to `pipeline.samples` (WebGL2 may clamp it). */
  msaaSamples: number;
  /** Plan 17 D4: environment theme; defaults to `'overcast'` so every existing caller is unchanged. */
  theme?: EnvTheme;
}

// Plan 08 G4: matched to the HDR sky's horizon band as it displays (measured, all tiers: fogged far
// geometry ~(205,210,220)). Geometry fog goes through the same grade as everything else on every tier.
// Exported for plan 19 D5: scene.dusk.test.ts pins the per-theme fog colours (createWorldScene is
// not observable under NullEngine — its HDRCubeTexture needs a real engine).
export const FOG_COLOR = new Color3(0.75, 0.77, 0.82);

// Plan 17 D4: the L2 'industrial' rain-grey steel-blue haze (#9aa4ad). It replaces FOG_COLOR for
// BOTH scene.clearColor and scene.fogColor under that theme, and the Bridge horizon clones
// scene.fogColor as its emissive — one constant retints the whole far plane.
export const RAIN_FOG_COLOR = new Color3(0.604, 0.643, 0.678);

// Plan 19 D5: the L3 'dusk' warm amber-grey haze — the finale's lower light. Like RAIN_FOG_COLOR it
// drives BOTH scene.clearColor and scene.fogColor under its theme, and the Bridge horizon clones
// scene.fogColor as its emissive, so this one constant retints the whole far plane (world.ts
// untouched).
export const DUSK_FOG_COLOR = new Color3(0.48, 0.38, 0.33);

/** Per-theme environment scalars — the branch numbers `createWorldScene` applies (see ENV_THEME_PARAMS). */
export interface EnvThemeParams {
  fogDensity: number;
  exposure: number;
  sunIntensity: number;
  sunDiffuse: readonly [number, number, number];
  environmentIntensity: number;
  skyZenith: Color3;
  skyHorizon: Color3;
  waterAlbedo: readonly [number, number, number];
  curves: {
    globalSaturation: number;
    shadowsHue: number;
    shadowsSaturation: number;
    highlightsHue: number;
    highlightsSaturation: number;
  };
}

/**
 * Plan 19 D5: the per-theme environment grade as ONE table — the single source for the numbers
 * `createWorldScene` branches on (the fog colours are the adjacent Color3 constants). Not observable
 * under NullEngine (createWorldScene cannot construct its HDRCubeTexture there), so
 * `scene.dusk.test.ts` pins these constants plus the unchanged sun direction vector from the source.
 * Dusk is strictly darker than BOTH existing themes on exposure, sun intensity and
 * environmentIntensity — the finale's lower light (plan 19 D5); the sun DIRECTION stays the shared
 * `Vector3(-0.35, -0.65, 0.55)` (the shadow-rig ortho box is measured against it — the plan pins it).
 */
export const ENV_THEME_PARAMS: Record<EnvTheme, EnvThemeParams> = {
  // Overcast daylight, held below the previous washed-out exposure.
  overcast: {
    fogDensity: 0.0072,
    exposure: 1.6,
    sunIntensity: 2.8,
    sunDiffuse: [1.0, 0.97, 0.9],
    environmentIntensity: 1.8, // the sole ambient since the hemi light went (plan 08 G1)
    skyZenith: new Color3(0.7, 0.72, 0.8),
    skyHorizon: FOG_COLOR,
    waterAlbedo: [0.07, 0.085, 0.095],
    // "War documentary overcast": slight desaturation, cool shadows, warm highlights.
    curves: { globalSaturation: -10, shadowsHue: 215, shadowsSaturation: 18, highlightsHue: 40, highlightsSaturation: 16 },
  },
  // Plan 17 D4: the L2 drained rain-grey regrade — thinner sun, dimmer exposure/IBL.
  industrial: {
    fogDensity: 0.0072,
    exposure: 1.55,
    sunIntensity: 2.6, // thinner sun behind the L2 cloud
    sunDiffuse: [0.92, 0.95, 1.0],
    environmentIntensity: 1.6,
    skyZenith: new Color3(0.56, 0.6, 0.66),
    skyHorizon: RAIN_FOG_COLOR,
    waterAlbedo: [0.07, 0.085, 0.095],
    // Drained, rain-leached — deeper desaturation, bluer shadows, near-neutral warm highlights
    // (rust/sodium light bleeding through cloud).
    curves: { globalSaturation: -22, shadowsHue: 210, shadowsSaturation: 26, highlightsHue: 35, highlightsSaturation: 8 },
  },
  // Plan 19 D5: dusk = the finale's lower light — strictly darker than both existing themes on
  // exposure/sun/env. Warm amber-grey sun, denser haze, near-black water, cool-shadow/warm-highlight
  // grade (the sun DIRECTION is unchanged — shadow-rig geometry, the plan pins it).
  dusk: {
    fogDensity: 0.0085, // thicker than the daytime themes' 0.0072: the haze sits lower
    exposure: 1.45,
    sunIntensity: 2.0, // warm, dim — the sun near the horizon
    sunDiffuse: [1.0, 0.72, 0.52],
    environmentIntensity: 1.35,
    skyZenith: new Color3(0.1, 0.15, 0.22),
    skyHorizon: DUSK_FOG_COLOR,
    waterAlbedo: [0.045, 0.04, 0.05], // the river goes near-black, reading the amber sky only as a sheen
    // Cool shadows against amber highlights: the dusk grade keeps the plan-08 hue split but pushes
    // the saturation extremes (legibility gate: the plan-17 D4 evidence-frame rule).
    curves: { globalSaturation: -18, shadowsHue: 255, shadowsSaturation: 30, highlightsHue: 22, highlightsSaturation: 28 },
  },
};

const SKY_GRADIENT_EASING = 1.5;

function paintSkyGradient(scene: Scene, params: EnvThemeParams): DynamicTexture {
  const width = 2;
  const height = 256;
  const midpoint = height / 2;
  const texture = new DynamicTexture('sky-gradient', { width, height }, scene, false, Texture.BILINEAR_SAMPLINGMODE);
  const context = texture.getContext();
  for (let y = 0; y < midpoint; y++) {
    const progress = y / (midpoint - 1);
    const eased = progress ** SKY_GRADIENT_EASING;
    context.fillStyle = Color3.Lerp(params.skyZenith, params.skyHorizon, eased).toHexString();
    context.fillRect(0, y, width, 1);
  }
  context.fillStyle = params.skyHorizon.toHexString();
  context.fillRect(0, midpoint, width, height - midpoint);
  texture.wrapU = Texture.CLAMP_ADDRESSMODE;
  texture.wrapV = Texture.CLAMP_ADDRESSMODE;
  // update(false) uploads canvas row 0 to texture v=1; Babylon's CreateSphere puts its TOP
  // pole at v=1 (legacy UV convention — NOT the WebGL top-left origin), so canvas row 0
  // (painted zenith) lands at the top pole. The dusk probe run verifies the orientation
  // every run (zenith cap + separation floor); the gradient path is shared across themes.
  // CompatibilityOptions.UseOpenGLOrientationForUV would flip it.
  texture.update(false);
  return texture;
}

function loadTexture(url: string, scene: Scene): Promise<Texture> {
  return new Promise((resolve, reject) => {
    const tex = new Texture(url, scene, false, false, Texture.TRILINEAR_SAMPLINGMODE, () => resolve(tex), reject);
    // Poly Haven 1k tiles: repeat handled by the material users via uScale/vScale.
  });
}

/** PBR material from Poly Haven diff/arm/nor_gl sets (arm = AO/roughness/metalness). */
export function pbrFromSet(
  scene: Scene,
  albedo: Texture,
  arm: Texture,
  normal: Texture,
  uScale: number,
  vScale: number,
): PBRMaterial {
  const mat = new PBRMaterial('pbr', scene);
  for (const texture of [albedo, arm, normal]) {
    texture.uScale = uScale;
    texture.vScale = vScale;
    texture.anisotropicFilteringLevel = 4;
  }
  arm.gammaSpace = false;
  normal.gammaSpace = false;
  mat.albedoTexture = albedo;
  mat.metallicTexture = arm;
  mat.useRoughnessFromMetallicTextureGreen = true;
  mat.useRoughnessFromMetallicTextureAlpha = false;
  mat.useMetallnessFromMetallicTextureBlue = true;
  mat.ambientTexture = arm; // AO from red channel
  mat.useAmbientOcclusionFromMetallicTextureRed = true;
  mat.useAmbientInGrayScale = true;
  mat.bumpTexture = normal;
  mat.bumpTexture.level = 0.9;
  mat.environmentIntensity = 0.9;
  mat.directIntensity = 1.0;
  return mat;
}

// ---------------------------------------------------------------------------
// Plan 05 P5.5 — procedural PBR character materials (0 asset bytes).
//
// The soldier and the boss are ONE merged mesh with ONE material whose UVs sit in the atlas tiles
// defined by `soldierAtlas.ts` (P5.2/P5.4). This section is the OTHER half of that contract: it
// paints those same tiles into a DynamicTexture. Single source = the tile NAMES: `MATERIAL_TILES`
// is typed `readonly AtlasTile[]` (the union soldierAtlas owns) and the painter reads `tileRect()`
// straight out of soldierAtlas, so geometry and material cannot disagree. That is asserted in
// `scene.materials.test.ts` against the tiles `buildSoldier`/`buildBoss` actually remapped into.
//
// Determinism: no Math.random anywhere. The blotch field is value noise over an integer lattice
// hashed by `hashValue` (a murmur-style 32-bit finaliser), so a given tile always paints byte-for-byte
// identically — two runs produce the same texture, which keeps the gate screenshots comparable.
//
// NullEngine guard: unit tests run in node, where there is no OffscreenCanvas. `paintAtlas` then
// returns null and the material keeps the FLAT_ALBEDO colour — byte-identical to the pre-P5.5
// materials (the same guard shape as `buildShadowTexture` in actors.ts). The canvas path is exercised
// by tools/smoke.mjs + tools/inspect.mjs screenshots, not by the unit suite (plan 05 §P5.5: accepted).
// ---------------------------------------------------------------------------

/**
 * Atlas resolution. 256² keeps the four procedural characters at ~800 KB of RGBA each with zero
 * download cost; each of the 2×6 tiles is 128×43 texels, and at the gameplay camera a soldier is
 * ≈150 px tall with a uniform panel of ≈30 px on screen — mip 0–2, i.e. above the ATLAS_PAD inset,
 * so the pad in soldierAtlas.ts is what keeps neighbouring tiles from bleeding.
 */
export const ATLAS_TEXTURE_SIZE = 256;

/**
 * Which tiles each character material paints. The names ARE the atlas contract (soldierAtlas
 * `TILE_GRID` is the source of truth); the test asserts these lists equal the tile set the geometry
 * remapped into, so an unpainted tile or a painted-but-unused tile fails the suite.
 */
export const MATERIAL_TILES = {
  squad: ['uniform', 'skin', 'helmet', 'weapon', 'gear', 'accent'] as const satisfies readonly AtlasTile[],
  enemy: ['uniform', 'skin', 'helmet', 'weapon', 'gear', 'accent'] as const satisfies readonly AtlasTile[],
  boss: ['bossPlate', 'bossHide', 'bossClub', 'bossTrim', 'bossBone', 'bossGlow'] as const satisfies readonly AtlasTile[],
};

/**
 * Flat albedos for the no-canvas (NullEngine) path — the pre-P5.5 values, verbatim, so the unit
 * suite keeps the exact look it was pinned against. Linear (Babylon Color3), not sRGB bytes.
 */
export const FLAT_ALBEDO: Record<'squad' | 'enemy' | 'boss', readonly [number, number, number]> = {
  squad: [0.33, 0.37, 0.27],
  enemy: [0.45, 0.24, 0.2],
  boss: [0.16, 0.17, 0.19],
};

/** Base PBR params (plan 05 §P5.5: stay near the pre-P5.5 numbers — the texture carries colour, not metal/rough). */
export const MATERIAL_PARAMS = {
  squad: { metallic: 0.05, roughness: 0.85 },
  enemy: { metallic: 0.05, roughness: 0.8 },
  boss: { metallic: 0.8, roughness: 0.65 }, // plan 08 G5: plate reads as metal (ORM scales it per tile)
};

/** Metallic base when the character ORM map is on (canvas path): the blue channel scales it per tile. */
export const CHARACTER_ORM_METALLIC = 0.6;

/** sRGB byte triple — the atlas context writes 0..255 (BaseTexture.gammaSpace default ⇒ sampled as sRGB). */
type Rgb = [number, number, number];

/**
 * Tile painter. (a, b) ∈ [0, 1)² are tile-local coordinates (a = +u, b = +v DOWNWARD in canvas
 * space), `e` is the distance to the nearest tile edge in the same units (drives the worn-edge
 * highlights — remapUVs normalises every part to FILL its tile, so a part's silhouette border lands
 * on the tile border). Writes into `out` (0..255, alpha is filled by the writer).
 */
export type TilePainter = (a: number, b: number, e: number, out: Rgb) => void;

/** 32-bit integer hash → [0, 1). Deterministic, no RNG, no sin (see the determinism note above). */
export function hashValue(x: number, y: number, seed: number): number {
  let h = Math.imul(x | 0, 0x27d4eb2d) + Math.imul(y | 0, 0x165667b1) + Math.imul(seed | 0, 0x9e3779b1);
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

const smoothstep01 = (t: number): number => t * t * (3 - 2 * t);

/** Classic edge0/edge1 smoothstep — used for the camo/scuff ramps (not the noise interpolation). */
function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/**
 * Bilinear value noise over a `cells`-point lattice on the unit square, smoothstep-interpolated.
 * `cells` is the BLOTCH SCALE: ~8 across a tile (finer, plan 08 G5) reads as camo panels at the gameplay camera.
 */
export function valueNoise(a: number, b: number, cells: number, seed: number): number {
  const fx = a * cells;
  const fy = b * cells;
  const ix = Math.floor(fx);
  const iy = Math.floor(fy);
  const tx = smoothstep01(fx - ix);
  const ty = smoothstep01(fy - iy);
  const n00 = hashValue(ix, iy, seed);
  const n10 = hashValue(ix + 1, iy, seed);
  const n01 = hashValue(ix, iy + 1, seed);
  const n11 = hashValue(ix + 1, iy + 1, seed);
  const nx = n00 + (n10 - n00) * tx;
  const ny = n01 + (n11 - n01) * tx;
  return nx + (ny - nx) * ty;
}

const mix = (lo: number, hi: number, t: number): number => lo + (hi - lo) * t;
const clamp255 = (v: number): number => (v < 0 ? 0 : v > 255 ? 255 : v | 0);

/** Uneven band stops ⇒ the four camo tones do not split the tile evenly (real camo is lopsided). */
export const CAMO_STOPS = [0.36, 0.47, 0.62];

/**
 * The camo blotch field: two octaves of value noise plus a fine dither that breaks up the band
 * edges. Exported for the test (determinism + all four tones are actually reached); `blotchCamo`
 * bands it with CAMO_STOPS.
 */
export function camoFieldValue(a: number, b: number, seed: number): number {
  return (
    0.62 * valueNoise(a, b, 8, seed) +
    0.38 * valueNoise(a, b, 16, seed + 1) +
    0.05 * (valueNoise(a, b, 64, seed + 2) - 0.5)
  );
}

/** 4-tone blotch camo: two octaves of value noise, banded, with a fine dither breaking the edges. */
function blotchCamo(tones: Rgb[], seed: number): TilePainter {
  return (a, b, _e, out) => {
    const field = camoFieldValue(a, b, seed);
    let tone = 0;
    while (tone < CAMO_STOPS.length && field >= CAMO_STOPS[tone]!) tone++;
    const t = tones[tone]!;
    // Fabric shading: a low-frequency light/dark pass so panels are not uniform swatches.
    const shade = 0.9 + 0.2 * valueNoise(a, b, 3, seed + 4);
    out[0] = t[0] * shade;
    out[1] = t[1] * shade;
    out[2] = t[2] * shade;
  };
}

/** Wear band along the tile border: parts' silhouette edges catch the light (also drives the ORM map). */
function edgeWear(a: number, b: number, e: number, seed: number, band = 0.07): number {
  if (e > band) return 0;
  const t = 1 - e / band;
  return t * t * (0.45 + 0.55 * valueNoise(a * 7, b * 7, 26, seed));
}

// Palettes (sRGB bytes), plan 08 G5. Real olive drab sits around sRGB 60-110; under the G1 lighting
// (strong daylight exposure) anything above that goes chalky, so the squad is dark woodland camo. The
// enemy is a different army: a grey-blue camo family, with red ONLY on the armband + helmet band
// (the 'accent' tile, which the geometry now uses for nothing else).
const SQUAD = {
  camo: [
    [78, 82, 64],
    [92, 96, 74],
    [62, 68, 51],
    [86, 78, 60],
  ] as Rgb[],
  skin: [170, 130, 104] as Rgb,
  helmet: [60, 66, 52] as Rgb,
  helmetScuff: [92, 94, 78] as Rgb,
  weapon: [56, 58, 62] as Rgb,
  weaponSheen: [118, 120, 126] as Rgb,
  gear: [74, 70, 56] as Rgb,
  gearStrap: [96, 88, 70] as Rgb,
  accent: [112, 98, 70] as Rgb,
  accentEdge: [150, 136, 108] as Rgb,
};

const ENEMY = {
  camo: [
    [76, 86, 100],
    [102, 112, 126],
    [52, 60, 74],
    [68, 72, 82],
  ] as Rgb[],
  skin: [160, 126, 104] as Rgb,
  helmet: [56, 62, 74] as Rgb,
  helmetScuff: [90, 96, 110] as Rgb,
  weapon: [46, 48, 52] as Rgb,
  weaponSheen: [104, 106, 114] as Rgb,
  gear: [46, 48, 56] as Rgb,
  gearStrap: [68, 70, 80] as Rgb,
  accent: [168, 30, 26] as Rgb,
  accentEdge: [214, 92, 78] as Rgb,
};

const BOSS = {
  plate: [58, 62, 70] as Rgb,
  plateWear: [128, 132, 140] as Rgb,
  hide: [66, 56, 50] as Rgb,
  hideMottle: [92, 78, 68] as Rgb,
  club: [78, 58, 40] as Rgb,
  clubGrain: [52, 38, 26] as Rgb,
  trim: [54, 54, 58] as Rgb,
  bone: [196, 190, 170] as Rgb,
  boneGrime: [132, 122, 104] as Rgb,
};

function helmetPainter(p: typeof SQUAD | typeof ENEMY, seed: number): TilePainter {
  return (a, b, e, out) => {
    const scuff = smoothstep(0.6, 0.78, valueNoise(a, b, 12, seed));
    const mottle = 0.88 + 0.24 * valueNoise(a, b, 34, seed + 1);
    const wear = edgeWear(a, b, e, seed + 2, 0.05);
    for (let c = 0; c < 3; c++) {
      const base = mix(p.helmet[c]!, p.helmetScuff[c]!, scuff) * mottle;
      out[c] = mix(base, p.helmetScuff[c]!, wear);
    }
  };
}

function weaponPainter(p: typeof SQUAD | typeof ENEMY, seed: number): TilePainter {
  return (a, b, _e, out) => {
    // One soft sheen band across the receiver plus fine machining noise.
    const d = (b - 0.34) * 5.5;
    const sheen = Math.exp(-d * d) * 0.55;
    const grain = 0.85 + 0.3 * valueNoise(a, b, 96, seed);
    for (let c = 0; c < 3; c++) {
      out[c] = mix(p.weapon[c]! * grain, p.weaponSheen[c]!, sheen);
    }
  };
}

function gearPainter(p: typeof SQUAD | typeof ENEMY, seed: number): TilePainter {
  return (a, b, _e, out) => {
    const weave = 0.9 + 0.2 * valueNoise(a, b, 128, seed);
    const strap = valueNoise(a * 0.5, b * 0.5, 6, seed + 1) > 0.72 ? 1 : 0;
    for (let c = 0; c < 3; c++) {
      out[c] = strap ? p.gearStrap[c]! * weave : p.gear[c]! * weave;
    }
  };
}

function accentPainter(p: typeof SQUAD | typeof ENEMY, seed: number): TilePainter {
  return (a, b, e, out) => {
    const grit = 0.9 + 0.2 * valueNoise(a, b, 48, seed);
    const wear = edgeWear(a, b, e, seed + 1);
    for (let c = 0; c < 3; c++) {
      out[c] = mix(p.accent[c]! * grit, p.accentEdge[c]!, wear);
    }
  };
}

/** The soldier / enemy atlas: same layout, different palette (one builder, two textures). */
function characterTilePaint(faction: 'squad' | 'enemy'): Partial<Record<AtlasTile, TilePainter>> {
  const p = faction === 'squad' ? SQUAD : ENEMY;
  const seed = faction === 'squad' ? 11 : 47;
  return {
    uniform: blotchCamo(p.camo, seed),
    skin: (a, b, _e, out) => {
      const flush = 0.94 + 0.12 * valueNoise(a, b, 7, seed + 5);
      const grain = 0.97 + 0.06 * valueNoise(a, b, 128, seed + 6);
      const cheek = smoothstep(0.55, 0.85, valueNoise(a, b, 4, seed + 7));
      for (let c = 0; c < 3; c++) {
        out[c] = p.skin[c]! * flush * grain * (1 - 0.08 * cheek * (c === 0 ? -1 : 1));
      }
    },
    helmet: helmetPainter(p, seed + 20),
    weapon: weaponPainter(p, seed + 30),
    gear: gearPainter(p, seed + 40),
    accent: accentPainter(p, seed + 50),
  };
}

/** The boss atlas: plate metal with worn edges, hide, wood club, iron trim, bone studs. */
function bossTilePaint(): Partial<Record<AtlasTile, TilePainter>> {
  const seed = 91;
  return {
    bossPlate: (a, b, e, out) => {
      const mottle = 0.86 + 0.28 * valueNoise(a, b, 8, seed);
      const scratch = valueNoise(a, b, 140, seed + 1) > 0.82 ? 1.18 : 1;
      const wear = Math.min(1, edgeWear(a, b, e, seed + 2) * 1.6);
      for (let c = 0; c < 3; c++) {
        out[c] = mix(BOSS.plate[c]! * mottle * scratch, BOSS.plateWear[c]!, wear);
      }
    },
    bossHide: (a, b, e, out) => {
      const mottle = smoothstep(0.35, 0.7, valueNoise(a, b, 6, seed + 10));
      const grain = 0.88 + 0.24 * valueNoise(a, b, 90, seed + 11);
      const scab = edgeWear(a, b, e, seed + 12, 0.04);
      for (let c = 0; c < 3; c++) {
        out[c] = mix(BOSS.hide[c]!, BOSS.hideMottle[c]!, mottle) * grain * (1 - 0.25 * scab);
      }
    },
    bossClub: (a, b, _e, out) => {
      // Grain = stretched noise along the tile's v (the club is a cylinder whose UVs run lengthwise).
      const rings = 0.5 + 0.5 * Math.sin((b * 22 + 3 * valueNoise(a, b, 5, seed + 20)) * Math.PI * 2);
      const grime = 0.85 + 0.3 * valueNoise(a, b, 40, seed + 21);
      for (let c = 0; c < 3; c++) {
        out[c] = mix(BOSS.club[c]! * grime, BOSS.clubGrain[c]!, rings * 0.6);
      }
    },
    bossTrim: (a, b, e, out) => {
      const wear = edgeWear(a, b, e, seed + 30);
      const grit = 0.86 + 0.28 * valueNoise(a, b, 60, seed + 31);
      for (let c = 0; c < 3; c++) {
        out[c] = mix(BOSS.trim[c]! * grit, BOSS.plateWear[c]!, wear * 0.8);
      }
    },
    bossGlow: (_a, _b, _e, out) => {
      out[0] = 48; // dark housing; the emissive map (bossEmissivePaint) is what lights it
      out[1] = 40;
      out[2] = 36;
    },
    bossBone: (a, b, e, out) => {
      const grime = smoothstep(0.4, 0.75, valueNoise(a, b, 9, seed + 40));
      const tip = edgeWear(a, b, e, seed + 41, 0.09);
      for (let c = 0; c < 3; c++) {
        out[c] = mix(BOSS.bone[c]!, BOSS.boneGrime[c]!, grime) * (1 - 0.3 * tip);
      }
    },
  };
}

/**
 * The boss's ORM companion map: green = roughness factor, blue = metallic factor (both MULTIPLY the
 * material scalars — see the openpbr shader: `specular_roughness *= roughnessFromTexture`). Painting
 * it per tile is what gives the plate roughness variation and the worn, polished edges; red is unused
 * (no AO read) and stays mid-grey. Green/blue are BYTE-domain (× 255 behind a `Math.min(1, ·)` guard,
 * the `characterOrmPaint` domain — audit #4: a 0..1 factor is truncated to 0 by `clamp255`, i.e. a
 * glossy-plastic boss). Exported so the byte domain is unit-testable under NullEngine, like
 * `paintAtlasTile`.
 */
export function bossOrmPaint(): Partial<Record<AtlasTile, TilePainter>> {
  const seed = 91;
  const orm = (rough: number, metal: number): TilePainter => (a, b, e, out) => {
    const vary = 0.85 + 0.3 * valueNoise(a, b, 9, seed + 60);
    const wear = edgeWear(a, b, e, seed + 2);
    out[0] = 128;
    out[1] = Math.min(1, rough * vary * (1 - 0.55 * wear)) * 255; // worn edges polish up
    out[2] = Math.min(1, metal * (0.85 + 0.3 * valueNoise(a, b, 7, seed + 61)) + 0.15 * wear) * 255;
  };
  return {
    bossPlate: orm(0.95, 0.7),
    bossHide: orm(1, 0.06),
    bossClub: orm(0.95, 0.05),
    bossTrim: orm(0.78, 0.45),
    bossBone: orm(0.9, 0.02),
    bossGlow: orm(0.5, 0.2),
  };
}

/** Boss emissive map: black everywhere except the slit tile (intensity comes from the material colour). */
function bossEmissivePaint(): Partial<Record<AtlasTile, TilePainter>> {
  const off: TilePainter = (_a, _b, _e, out) => {
    out[0] = out[1] = out[2] = 0;
  };
  return {
    bossPlate: off,
    bossHide: off,
    bossClub: off,
    bossTrim: off,
    bossBone: off,
    bossGlow: (_a, _b, _e, out) => {
      out[0] = out[1] = out[2] = 255;
    },
  };
}

/**
 * The character ORM companion map (plan 08 G5): green = roughness factor, blue = metallic factor
 * (both multiply the material scalars; metallic base is CHARACTER_ORM_METALLIC). Fabric stays rough
 * and non-metal, the helmet is slightly glossy, the weapon is gunmetal (0.6) with a worn sheen band.
 */
function characterOrmPaint(): Partial<Record<AtlasTile, TilePainter>> {
  const seed = 71;
  const orm = (rough: number, metal: number): TilePainter => (a, b, e, out) => {
    const vary = 0.9 + 0.2 * valueNoise(a, b, 12, seed);
    const wear = edgeWear(a, b, e, seed + 1);
    out[0] = 128;
    out[1] = Math.min(1, rough * vary * (1 - 0.3 * wear)) * 255;
    out[2] = Math.min(1, metal + 0.3 * wear * metal) * 255;
  };
  return {
    uniform: orm(1, 0.06),
    skin: orm(0.9, 0),
    helmet: orm(0.6, 0.1), // slightly glossy
    weapon: orm(0.7, 1),
    gear: orm(1, 0.15),
    accent: orm(1, 0.05),
  };
}

/**
 * Canvas rect of a tile in a `size`² atlas, derived from soldierAtlas's `tileRect` — the v flip is
 * the only arithmetic this module adds (canvas y runs top-down, UV v runs bottom-up).
 */
export function atlasTileCanvasRect(tile: AtlasTile, size = ATLAS_TEXTURE_SIZE): { x0: number; y0: number; x1: number; y1: number } {
  const r = tileRect(tile);
  return {
    x0: Math.floor(r.u0 * size),
    y0: Math.floor((1 - r.v1) * size),
    x1: Math.ceil(r.u1 * size),
    y1: Math.ceil((1 - r.v0) * size),
  };
}

/**
 * The two rects a tile's paint pass is defined by (impl gate F8).
 *
 * `core` is the region the GEOMETRY can ever sample: `tileUvBounds` shrinks the tile by ATLAS_PAD on
 * every side and `remapUVs` parks each part strictly inside it. `outer` is the region the painter
 * WRITES: the whole tile, i.e. the core plus the pad-wide gutter the inset reserves. With
 * `Texture.TRILINEAR_SAMPLINGMODE` + mipmaps on (see `paintAtlas`), a minified texel is an average of
 * FOUR texels from the level below, so the filter reaches past the core into that gutter. Whatever
 * sits there is therefore sampled by the tile it belongs to — and if the gutter still holds the
 * backing colour, or worse the neighbouring tile's paint, every mip level below 0 dilutes the tile
 * toward it (the classic atlas "dark seam / colour bleed" read on the camo panels).
 */
export function atlasTilePaintRects(
  tile: AtlasTile,
  size = ATLAS_TEXTURE_SIZE,
): { outer: { x0: number; y0: number; x1: number; y1: number }; core: { x0: number; y0: number; x1: number; y1: number }; pad: number } {
  const outer = atlasTileCanvasRect(tile, size);
  const coreUv = tileUvBounds(tile);
  // Derived from the UV bounds rather than assumed, so the gutter is exactly the inset the geometry
  // reserved — round() because a pad is not always a whole texel at a given atlas resolution.
  const pad = Math.max(0, Math.round(coreUv.u0 * size) - outer.x0);
  const core = {
    x0: outer.x0 + pad,
    y0: outer.y0 + pad,
    x1: outer.x1 - pad,
    y1: outer.y1 - pad,
  };
  return { outer, core, pad };
}

/**
 * Tile-local coordinate of a canvas pixel, CLAMPED to [0, 1] (impl gate F8). Pixels outside the core
 * — the pad gutter — get the coordinate of the nearest core edge, so the painter writes the tile's
 * own edge colour there instead of leaving backing/ neighbour paint for the trilinear filter to eat.
 * `span` is guarded against 0 so a degenerate tile cannot produce NaN coordinates.
 */
export function tileSampleCoord(px: number, core0: number, span: number): number {
  const t = (px - core0) / Math.max(1, span);
  return t < 0 ? 0 : t > 1 ? 1 : t;
}

/**
 * Write one tile's pixels into an atlas RGBA buffer (impl gate F8). Split out of `paintAtlas` so the
 * gutter rule — which is coordinate maths, not canvas — is unit-testable under NullEngine, where
 * `paintAtlas` itself bails at the OffscreenCanvas guard and the pixel writes are only ever seen in a
 * screenshot. The loop covers the WHOLE tile rect and maps the painter's (a, b) through the clamped
 * core, so the pad ring is same-tile edge paint rather than backing colour for the mip filter to eat.
 */
export function paintAtlasTile(data: Uint8ClampedArray, size: number, tile: AtlasTile, painter: TilePainter): void {
  const out: Rgb = [0, 0, 0];
  const { outer, core } = atlasTilePaintRects(tile, size);
  const w = core.x1 - core.x0;
  const h = core.y1 - core.y0;
  for (let y = outer.y0; y < outer.y1; y++) {
    for (let x = outer.x0; x < outer.x1; x++) {
      const a = tileSampleCoord(x, core.x0, w);
      const b = tileSampleCoord(y, core.y0, h);
      painter(a, b, Math.min(a, b, 1 - a, 1 - b), out);
      const i = (y * size + x) * 4;
      data[i] = clamp255(out[0]);
      data[i + 1] = clamp255(out[1]);
      data[i + 2] = clamp255(out[2]);
    }
  }
}

/**
 * Paint one atlas DynamicTexture. Returns null when the engine has no 2D canvas (NullEngine /
 * node) — the caller then keeps the flat albedo. Mipmaps on (the plan asks for a mip-friendly
 * atlas; `Texture.TRILINEAR_SAMPLINGMODE` + generateMipMaps is the same pairing the loaded
 * Poly Haven tiles use).
 */
function paintAtlas(scene: Scene, name: string, paint: Partial<Record<AtlasTile, TilePainter>>, bg: Rgb): DynamicTexture | null {
  if (typeof OffscreenCanvas === 'undefined') return null; // NullEngine guard: no 2D context available
  const tex = new DynamicTexture(name, ATLAS_TEXTURE_SIZE, scene, true, Texture.TRILINEAR_SAMPLINGMODE);
  const ctx = tex.getContext() as CanvasRenderingContext2D | null;
  if (!ctx || typeof ctx.createImageData !== 'function' || typeof ctx.putImageData !== 'function') {
    tex.dispose();
    return null;
  }
  const size = ATLAS_TEXTURE_SIZE;
  const img = ctx.createImageData(size, size);
  const data = img.data;
  for (let i = 0; i < data.length; i += 4) {
    data[i] = bg[0];
    data[i + 1] = bg[1];
    data[i + 2] = bg[2];
    data[i + 3] = 255;
  }
  for (const tile of Object.keys(paint) as AtlasTile[]) {
    const painter = paint[tile];
    if (!painter) continue;
    paintAtlasTile(data, size, tile, painter);
  }
  ctx.putImageData(img, 0, 0);
  tex.update();
  return tex;
}

const flatColor = (which: 'squad' | 'enemy' | 'boss'): Color3 => {
  const c = FLAT_ALBEDO[which];
  return new Color3(c[0], c[1], c[2]);
};

/**
 * Squad / enemy character material. Both factions share the builder and the atlas layout; only the
 * palette differs, so the enemy reads as the same kind of soldier in a different uniform.
 */
export function createCharacterMaterial(scene: Scene, faction: 'squad' | 'enemy', name: string): PBRMaterial {
  const mat = new PBRMaterial(name, scene);
  const params = MATERIAL_PARAMS[faction];
  mat.metallic = params.metallic;
  mat.roughness = params.roughness;
  const atlas = paintAtlas(scene, `${name}-atlas`, characterTilePaint(faction), [24, 24, 24]);
  if (!atlas) {
    mat.albedoColor = flatColor(faction); // NullEngine / no canvas: exactly the pre-P5.5 flat material
    return mat;
  }
  // albedoTexture MULTIPLIES albedoColor in the openpbr shader ⇒ white base, texture carries colour.
  mat.albedoColor = new Color3(1, 1, 1);
  mat.albedoTexture = atlas;
  const orm = paintAtlas(scene, `${name}-orm`, characterOrmPaint(), [128, 230, 15]);
  if (orm) {
    mat.metallic = CHARACTER_ORM_METALLIC;
    mat.metallicTexture = orm;
    mat.useRoughnessFromMetallicTextureGreen = true;
    mat.useMetallnessFromMetallicTextureBlue = true;
  }
  return mat;
}

/**
 * A per-soldier copy of a character material with its own albedo tint (plan 08 G5, skeletal squad).
 * Built by hand rather than `clone()`, which would duplicate the painted DynamicTextures per soldier;
 * the textures and flags are SHARED, so the defines are identical and no extra shader compiles.
 */
export function tintCharacterMaterial(base: PBRMaterial, name: string, tint: readonly [number, number, number]): PBRMaterial {
  const mat = new PBRMaterial(name, base.getScene());
  mat.metallic = base.metallic;
  mat.roughness = base.roughness;
  mat.environmentIntensity = base.environmentIntensity;
  mat.albedoTexture = base.albedoTexture;
  mat.metallicTexture = base.metallicTexture;
  mat.useRoughnessFromMetallicTextureGreen = base.useRoughnessFromMetallicTextureGreen;
  mat.useMetallnessFromMetallicTextureBlue = base.useMetallnessFromMetallicTextureBlue;
  mat.albedoColor = new Color3(base.albedoColor.r * tint[0], base.albedoColor.g * tint[1], base.albedoColor.b * tint[2]);
  return mat;
}

/** Boss material: plate metal (roughness variation + worn edges) over hide/club/trim/bone tiles. */
export function createBossMaterial(scene: Scene, name = 'boss'): PBRMaterial {
  const mat = new PBRMaterial(name, scene);
  mat.metallic = MATERIAL_PARAMS.boss.metallic;
  mat.roughness = MATERIAL_PARAMS.boss.roughness;
  const atlas = paintAtlas(scene, `${name}-atlas`, bossTilePaint(), [20, 20, 22]);
  if (!atlas) {
    mat.albedoColor = flatColor('boss');
    return mat;
  }
  mat.albedoColor = new Color3(1, 1, 1);
  mat.albedoTexture = atlas;
  const orm = paintAtlas(scene, `${name}-orm`, bossOrmPaint(), [128, 180, 90]);
  if (orm) {
    mat.metallicTexture = orm;
    mat.useRoughnessFromMetallicTextureGreen = true;
    mat.useMetallnessFromMetallicTextureBlue = true;
  }
  // Eye/vent slits: the emissive COLOUR starts black (no glow) and is driven per frame by
  // applyBossGlow (actors.ts) as a pure function of the slam telegraph.
  const emissive = paintAtlas(scene, `${name}-emissive`, bossEmissivePaint(), [0, 0, 0]);
  if (emissive) {
    mat.emissiveTexture = emissive;
    mat.emissiveColor = new Color3(0, 0, 0);
  }
  return mat;
}

// ---------------------------------------------------------------------------
// Plan 05 P5.6 — character shadows (High tier only, one soft cascade).
//
// One 1024² PCF map on the existing sun DirectionalLight — NOT a second light: the shadow term in
// the openpbr shader attenuates the light that OWNS the generator, so a 0-intensity shadow light
// would cast nothing you could see, and a second sun would disagree with the shading direction.
// Soft and low-contrast by design (plan 05 Design 5): largest PCF kernel (5×5) + moderate darkness,
// because a hard edge would read *less* realistic than the blob it replaces.
//
// Casters: the squad mesh + the boss body. Enemies keep blob shadows on every tier (dossier §5) and
// are deliberately NOT casters. Receivers: the bridge deck + rails/skirt (a caster with no receiver
// renders nothing — r1 P1). The membership lives on the rig, so the probe can state it as fact.
//
// Determinism (the pause-frozen-screenshot contract from P5.3): the only time-varying input to the
// shadow path is the squad position the rig tracks, and that position is TEXEL-SNAPPED — a sub-texel
// move does not even touch the light. The map itself refreshes every frame, so under the inspect
// pause the depth pass is fed byte-identical inputs (frozen squad + closed-form instance matrices)
// and repeats identically; nothing in this path reads a clock, a frame counter or render-layer state.
// ---------------------------------------------------------------------------

/** Shadow map resolution (plan 05 Design 5: one 1024² PCF map). Mirrored by tools/inspect.mjs. */
export const SHADOW_MAP_SIZE = 1024;
/**
 * Half-extent of the square light-space ortho box, metres. Sized to cover the crowd (≈9 m wide),
 * the ≈33 m run from the squad to the boss standoff (which projects to ≈18 m across the light's x/y
 * axes at this sun angle) and the 4.5 m brute, with margin: the shadow-map boundary is a visible
 * artifact source (edge texels of the depth map), so the box deliberately overshoots the scene.
 * 36 m over 1024 texels is still ≈3.5 cm/texel — soft-PCF territory.
 */
export const SHADOW_ORTHO_HALF = 18;
/** How far up the light axis the eye sits from the tracked anchor (SHADOW_DEPTH_HALF around it). */
const SHADOW_LIGHT_DISTANCE = 45;
/** Depth margin along the light axis: covers the crowd plus the ≈4.5 m brute without clipping. */
const SHADOW_DEPTH_HALF = 26;
/** Anchor offset ahead of squadZ: centres the box between the squad and the boss standoff (≈30 m). */
const SHADOW_ANCHOR_AHEAD = 13.5;
/** Anchor height: mid-torso, so the box is not tilted toward the sky. */
const SHADOW_ANCHOR_Y = 1;
/**
 * 0 = black, 1 = none (Babylon semantics). Moderate for an overcast fill (plan 05 Design 5).
 * Measured on the High headless shot (z462_boss, 48 soldiers): at darkness 0 the shadow removes up
 * to 37/255 of LOCAL luminance on the deck, because the sun itself is only ≈22/255 of the deck's
 * luminance (IBL dominates; the hemi light is gone, plan 08 G1) — so 0.25 lands at ≈26/255, a soft grey read, not a
 * hole in the ground. Re-measured after G1: the crowd lobe ≈0.86 of open deck; the blob alphas in
 * actors.ts `buildShadowTexture` are tuned to that ratio. Hardness is handled by the 5×5 PCF kernel, not by this number.
 */
const SHADOW_DARKNESS = 0.25;
/** Depth bias + a small normal bias: no acne on the deck, no peter-pan at 3.5 cm texels. */
const SHADOW_BIAS = 0.0008;
const SHADOW_NORMAL_BIAS = 0.02;

/** The probe-facing surface of the shadow cascade (truthful membership facts only). */
export interface CharacterShadows {
  readonly mapSize: number;
  readonly enabled: boolean;
  setEnabled(on: boolean): void;
  addCaster(mesh: Mesh): void;
  addReceiver(mesh: AbstractMesh): void;
  casterNames(): string[];
  casterMeshes(): Mesh[];
  receiverNames(): string[];
  /** Track the squad (world metres). Texel-snapped; a sub-texel move changes nothing at all. */
  follow(squadWorldX: number, squadWorldZ: number): void;
  debugState(): { enabled: boolean; mapSize: number; casters: string[]; receivers: string[] };
}

class CharacterShadowRig implements CharacterShadows {
  readonly mapSize = SHADOW_MAP_SIZE;
  private readonly generator: ShadowGenerator;
  private readonly receivers: AbstractMesh[] = [];
  /** Last applied light-space eye position (the snap grid is anchored at the world origin). */
  private snappedX = Number.NaN;
  private snappedY = Number.NaN;
  private snappedZ = Number.NaN;

  constructor(private readonly light: DirectionalLight) {
    const generator = new ShadowGenerator(SHADOW_MAP_SIZE, light);
    // PCF, largest kernel available (5×5). Where shadow samplers are missing Babylon silently
    // downgrades `filter` to Poisson sampling — a device decision, not a gate claim.
    generator.usePercentageCloserFiltering = true;
    generator.filteringQuality = ShadowGenerator.QUALITY_HIGH;
    generator.darkness = SHADOW_DARKNESS;
    generator.bias = SHADOW_BIAS;
    generator.normalBias = SHADOW_NORMAL_BIAS;
    generator.forceBackFacesOnly = false;
    // Fade the shadow out at the frustum border instead of clipping it there (the border otherwise
    // reads as a faint hard-edged smudge on the deck — measured at ±18 m around the anchor).
    generator.frustumEdgeFalloff = 0.25;
    this.generator = generator;

    // The map re-renders every frame. Determinism under the inspect pause comes from the INPUTS, not
    // from skipping the pass: the only time-varying input is the (frozen-under-pause) squad position
    // the rig tracks plus the closed-form instance matrices (P5.3/P5.4), so repeated paused frames
    // get the same depth image. Render-on-change (`REFRESHRATE_RENDER_ONCE`) would have made the
    // gate's `shadowTriOverlap` 0 by construction rather than by engine behaviour, and is therefore
    // not used — but `invalidate()` stays, so a caster/receiver/enable change is always in the next frame.
    const map = generator.getShadowMap();
    if (map) map.refreshRate = RenderTargetTexture.REFRESHRATE_RENDER_ONEVERYFRAME;

    // Authored, fixed frustum: autoUpdateExtends would size the box off the casters' bounds, and
    // those bob with the motion ⇒ per-frame texel resampling = swimming shadows.
    light.autoUpdateExtends = false;
    light.shadowOrthoScale = 0;
    light.orthoLeft = -SHADOW_ORTHO_HALF;
    light.orthoRight = SHADOW_ORTHO_HALF;
    light.orthoBottom = -SHADOW_ORTHO_HALF;
    light.orthoTop = SHADOW_ORTHO_HALF;
    light.shadowMinZ = SHADOW_LIGHT_DISTANCE - SHADOW_DEPTH_HALF;
    light.shadowMaxZ = SHADOW_LIGHT_DISTANCE + SHADOW_DEPTH_HALF;
    light.shadowEnabled = true;
  }

  get enabled(): boolean {
    return this.light.shadowEnabled && !!this.generator.getShadowMap();
  }

  /**
   * Boot-time lever (`?noshadow=1`, debug builds only): `shadowEnabled` is the flag the shadow scene
   * component checks when it gathers render targets, so turning it off takes the whole shadow pass
   * out of the frame — exactly the comparison the P5.6 overlap measurement needs.
   */
  setEnabled(on: boolean): void {
    if (this.light.shadowEnabled === on) return;
    this.light.shadowEnabled = on;
    this.invalidate();
  }

  addCaster(mesh: Mesh): void {
    this.generator.addShadowCaster(mesh);
    this.invalidate();
  }

  addReceiver(mesh: AbstractMesh): void {
    if (this.receivers.includes(mesh)) return;
    mesh.receiveShadows = true; // the flag lives on AbstractMesh, unlike the caster render list
    this.receivers.push(mesh);
  }

  /** The actual shadow-map render list — what the engine will draw, not what we meant to add. */
  casterNames(): string[] {
    return this.casterMeshes().map((mesh) => mesh.name);
  }

  casterMeshes(): Mesh[] {
    // Everything registered via addCaster() is a Mesh (the rig's only caster source); Babylon types
    // the render list AbstractMesh[].
    return (this.generator.getShadowMap()?.renderList ?? []) as Mesh[];
  }

  receiverNames(): string[] {
    return this.receivers.map((mesh) => mesh.name);
  }

  follow(squadWorldX: number, squadWorldZ: number): void {
    // Rebuild the light basis from the live direction (Babylon: LookAtLH(pos, pos + dir, up)).
    const d = this.light.direction;
    const len = Math.hypot(d.x, d.y, d.z) || 1;
    const zx = d.x / len;
    const zy = d.y / len;
    const zz = d.z / len;
    // right = normalize(up × z) with up = (0,1,0) ⇒ (zz, 0, −zx); up′ = z × right.
    let rx = zz;
    let rz = -zx;
    const rlen = Math.hypot(rx, rz);
    if (rlen < 1e-6) {
      rx = 1;
      rz = 0;
    } else {
      rx /= rlen;
      rz /= rlen;
    }
    const ry = 0;
    const ux = zy * rz - zz * ry;
    const uy = zz * rx - zx * rz;
    const uz = zx * ry - zy * rx;

    // Snap the EYE's light-space coordinates (not the anchor's): a static world point then lands on
    // the same texels between moves, which is what kills the swimming. eye = anchor − ẑ·DIST, and
    // x̂/ŷ ⊥ ẑ, so only the depth coordinate carries the distance term.
    const ax = squadWorldX;
    const ay = SHADOW_ANCHOR_Y;
    const az = squadWorldZ + SHADOW_ANCHOR_AHEAD;
    const texel = (2 * SHADOW_ORTHO_HALF) / this.mapSize;
    const sx = roundTo(ax * rx + ay * ry + az * rz, texel);
    const sy = roundTo(ax * ux + ay * uy + az * uz, texel);
    const sz = roundTo(ax * zx + ay * zy + az * zz - SHADOW_LIGHT_DISTANCE, texel);
    if (sx === this.snappedX && sy === this.snappedY && sz === this.snappedZ) return; // sub-texel: nothing moves
    this.snappedX = sx;
    this.snappedY = sy;
    this.snappedZ = sz;
    // Reconstruct the world position from the snapped light-space coordinates (basis is orthonormal).
    this.light.position.set(
      sx * rx + sy * ux + sz * zx,
      sx * ry + sy * uy + sz * zy,
      sx * rz + sy * uz + sz * zz,
    );
    this.invalidate();
  }

  /** Keep the invalidation hook: a caster/receiver/enable change must be in the next frame. */
  private invalidate(): void {
    this.generator.getShadowMap()?.resetRefreshCounter();
  }

  debugState(): { enabled: boolean; mapSize: number; casters: string[]; receivers: string[] } {
    return {
      enabled: this.enabled,
      mapSize: this.generator.getShadowMap() ? this.mapSize : 0,
      casters: this.casterNames(),
      receivers: this.receiverNames(),
    };
  }
}

const roundTo = (v: number, step: number): number => Math.round(v / step) * step;

/**
 * The P5.1/P5.6 shadow-caster triangle term: Σ caster `getTotalIndices()/3 × max(1, thinInstanceCount)`
 * (plan 05 Design 6). The `max(1, ·)` keeps the non-thin boss caster in the sum — and note that a mesh
 * which never had an instance buffer reports a non-finite `thinInstanceCount`, so the guard is written
 * as a comparison, not a `?? 0` that the types would call dead: a NaN here would poison the whole
 * budget sum (locked by scene.materials.test.ts). Casters are asserted indexed at boot in main.ts.
 */
export function shadowCasterTriangles(casters: readonly Mesh[]): number {
  let sum = 0;
  for (const mesh of casters) {
    const instances = mesh.thinInstanceCount > 0 ? mesh.thinInstanceCount : 1;
    sum += (mesh.getTotalIndices() / 3) * instances;
  }
  return sum;
}

/**
 * Build the High-tier cascade on the given light. `createWorldScene` calls this only when
 * `caps.realShadows`; exported so the unit suite can construct it under a NullEngine.
 */
export function createCharacterShadowRig(light: DirectionalLight): CharacterShadows {
  return new CharacterShadowRig(light);
}

export async function createWorldScene(engine: AbstractEngine, options: WorldSceneOptions): Promise<WorldScene> {
  const scene = new Scene(engine);
  const theme: EnvTheme = options.theme ?? 'overcast';
  const params = ENV_THEME_PARAMS[theme];
  // The sky covers the clear colour; this is the fog/haze that meets its horizon. Plan 19 D5:
  // dusk brings its own haze the way industrial brought RAIN_FOG_COLOR. The horizon sky stop
  // IS this colour (the theme-table pins hold the reference), so one constant owns the seam.
  const skyFog = params.skyHorizon;
  scene.clearColor = new Color4(skyFog.r, skyFog.g, skyFog.b, 1);

  // Depth fog: hides the track's end, sells scale (research §5 item 2). Plan 19 D5: the dusk haze
  // is denser (0.0085) than the daytime themes' 0.0072.
  scene.fogMode = Scene.FOGMODE_EXP2;
  scene.fogDensity = params.fogDensity;
  scene.fogColor = skyFog;

  // ACES + subtle vignette + colour grade through image processing (plan 08 G1; Low needs no pipeline).
  const ip = scene.imageProcessingConfiguration;
  ip.toneMappingEnabled = true;
  ip.toneMappingType = ImageProcessingConfiguration.TONEMAPPING_ACES;
  // Plan 17 D4 dimmed the industrial exposure; dusk sits lower still — the
  // finale's lower light is strictly the darkest of the three.
  ip.exposure = params.exposure;
  ip.contrast = 1.12;
  ip.vignetteEnabled = true;
  ip.vignetteWeight = 1.6;
  ip.vignetteStretch = 0.4;
  ip.vignetteColor = new Color4(0, 0, 0, 0);
  // "War documentary overcast": slight desaturation, cool shadows, warm highlights. Rides the
  // existing image-processing pass, so it costs no render target on any tier. Plan 17 D4 branches
  // the grade per EnvTheme (the industrial variant is the drained rain-grey sibling); plan 19 D5
  // adds the dusk variant (cool shadows / amber highlights, the strongest split of the three).
  const curves = new ColorCurves();
  const cc = params.curves;
  curves.globalSaturation = cc.globalSaturation;
  curves.shadowsHue = cc.shadowsHue;
  curves.shadowsSaturation = cc.shadowsSaturation;
  curves.highlightsHue = cc.highlightsHue;
  curves.highlightsSaturation = cc.highlightsSaturation;
  ip.colorCurves = curves;
  ip.colorCurvesEnabled = true;

  // Lighting: overcast sky IBL (the only ambient — the hemi light double-counted it, plan 08 G1)
  // + one warm sun.
  // Plan 19 D5: the sun DIRECTION is UNCHANGED on every theme — the plan-05 shadow rig's ortho box
  // is measured against `Vector3(-0.35, -0.65, 0.55)`, and the plan pins it (regrade only, the
  // plan-17 precedent). Dusk dims it (2.0) and warms it (1.0, 0.62, 0.38).
  const sun = new DirectionalLight('sun', new Vector3(-0.35, -0.65, 0.55), scene);
  sun.intensity = params.sunIntensity; // plan 17 D4: thinner sun behind the L2 cloud
  sun.diffuse = new Color3(params.sunDiffuse[0], params.sunDiffuse[1], params.sunDiffuse[2]);

  // Plan 05 P5.6: the High-tier character shadow cascade, on the sun (one light ⇒ one shadow
  // direction, and the shader's shadow term attenuates the light that owns the generator). Built
  // only when the boot tier says so: on Low/Medium there is no render target at all and the blob
  // shadows stay (plan 04 P2d). Casters/receivers are registered by main.ts once the actors exist.
  const shadows: CharacterShadows | null = options.realShadows ? createCharacterShadowRig(sun) : null;

  // IBL from the CC0 equirect HDR (Poly Haven, see public/assets/README.md). Assigning the
  // environment texture before it finishes loading is fine: PBR picks it up on arrival.
  const environmentUrl = theme === 'overcast' ? 'assets/env_1k.hdr' : `assets/env_${theme}.hdr`;
  const env = new HDRCubeTexture(environmentUrl, scene, 256, false, true, false);
  env.name = 'env';
  scene.environmentTexture = env;
  scene.environmentIntensity = params.environmentIntensity; // the sole ambient since the hemi light went (plan 08 G1); plan 17 D4 dims the L2 IBL, plan 19 D5 dims dusk further

  const skyTexture = paintSkyGradient(scene, params);
  const skyMat = new StandardMaterial('sky', scene);
  skyMat.disableLighting = true;
  skyMat.emissiveTexture = skyTexture;
  skyMat.emissiveColor = Color3.Black();
  skyMat.diffuseColor = Color3.Black();
  skyMat.specularColor = Color3.Black();
  skyMat.fogEnabled = false;
  // BACKSIDE winding already shows the inside of the sphere; no culling toggle needed.
  const sky = MeshBuilder.CreateSphere('sky', { diameter: 900, segments: 32, sideOrientation: Mesh.BACKSIDE }, scene);
  sky.material = skyMat;
  sky.infiniteDistance = true;
  sky.isPickable = false;

  const camera = new FreeCamera('chase', new Vector3(0, 5.4, -9.5), scene);
  camera.minZ = 0.3;
  camera.maxZ = 1200; // the sky box sits beyond the water/horizon cards, so it must not out-depth them (plan 08 G4)
  camera.fov = 0.95;
  // Plan 08 G1: bloom only (threshold 0.9 so just emissives glow), no grain. The pipeline renders
  // off-screen, so it replaces the engine's antialias with `samples`; it reuses the scene's
  // imageProcessingConfiguration, so ACES/vignette/curves carry over.
  let pipeline: DefaultRenderingPipeline | null = null;
  if (options.bloom) {
    pipeline = new DefaultRenderingPipeline('post', true, scene, [camera]);
    pipeline.samples = options.msaaSamples;
    pipeline.bloomEnabled = true;
    pipeline.bloomThreshold = 0.9;
    pipeline.bloomWeight = 0.35;
    pipeline.bloomKernel = options.msaaSamples > 2 ? 64 : 32; // Medium: smaller kernel, quarter-res
    pipeline.bloomScale = options.msaaSamples > 2 ? 0.5 : 0.25;
    pipeline.grainEnabled = false;
  }
  scene.skipPointerMovePicking = true;
  scene.skipPointerDownPicking = true;
  scene.skipPointerUpPicking = true;

  // Materials: bridge + actors. Textures resolve async; materials render flat until then.
  const [asphaltD, asphaltA, asphaltN, concreteD, concreteA, concreteN, metalD, metalA, metalN, waterN] = await Promise.all([
    loadTexture('assets/asphalt_diff_1k.jpg', scene),
    loadTexture('assets/asphalt_arm_1k.jpg', scene),
    loadTexture('assets/asphalt_nor_gl_1k.jpg', scene),
    loadTexture('assets/concrete_diff_1k.jpg', scene),
    loadTexture('assets/concrete_arm_1k.jpg', scene),
    loadTexture('assets/concrete_nor_gl_1k.jpg', scene),
    loadTexture('assets/metal_diff_1k.jpg', scene),
    loadTexture('assets/metal_arm_1k.jpg', scene),
    loadTexture('assets/metal_normal.png', scene),
    loadTexture('assets/water_nor_gl_1k.jpg', scene),
  ]);
  const asphalt = pbrFromSet(scene, asphaltD, asphaltA, asphaltN, 2.2, 9);
  asphalt.roughness = 0.72;
  const concrete = pbrFromSet(scene, concreteD, concreteA, concreteN, 1.2, 1.2);
  concrete.albedoColor = new Color3(0.94, 0.94, 0.94);
  const metal = pbrFromSet(scene, metalD, metalA, metalN, 1, 1);
  metal.environmentIntensity = 1.1;
  metal.roughness = 0.65;

  // Plan 08 G4: murky water. Scale/offset of the normal map belong to the Bridge (sim.t driven); the
  // sky IBL gives it the overcast reflection, the fog hides the far edge. Plan 19 D5: the dusk river
  // goes near-black (0.045, 0.04, 0.05).
  const water = new PBRMaterial('water-mat', scene);
  water.albedoColor = new Color3(params.waterAlbedo[0], params.waterAlbedo[1], params.waterAlbedo[2]);
  water.metallic = 0;
  water.roughness = 0.16;
  water.bumpTexture = waterN;
  water.bumpTexture.level = 0.6;
  water.environmentIntensity = 1.0;

  // Character materials (plan 05 P5.5): procedural DynamicTexture atlases painting the SAME tiles
  // the authored geometry remapped into (soldierAtlas.ts). NullEngine ⇒ flat albedos (see above).
  const soldier = createCharacterMaterial(scene, 'squad', 'soldier');
  const enemy = createCharacterMaterial(scene, 'enemy', 'enemy');
  if (theme === 'dusk') {
    soldier.environmentIntensity = 1.4;
    enemy.environmentIntensity = 1.4;
  }
  const boss = createBossMaterial(scene, 'boss');

  const glowCache = new Map<string, StandardMaterial>();
  const glow = (color: Color3, alpha = 1): StandardMaterial => {
    const key = `${color.r},${color.g},${color.b},${alpha}`;
    let mat = glowCache.get(key);
    if (!mat) {
      mat = new StandardMaterial(`glow-${key}`, scene);
      mat.emissiveColor = color;
      mat.disableLighting = true;
      mat.alpha = alpha;
      mat.alphaMode = Constants.ALPHA_ADD; // additive: bright core, no sorting issues
      mat.backFaceCulling = false;
      glowCache.set(key, mat);
    }
    return mat;
  };

  return { scene, camera, materials: { asphalt, concrete, water, metal, soldier, enemy, boss, glow }, shadows, pipeline };
}
